import { useEffect, useState } from 'react';
import type { InspectionTour, InspectionTourRequest, TourVerdict } from '../api/inspections';
import { ca } from '../i18n/ca';
import { useCanReview } from './reviewAccess';
import { tourReason } from './tourReason';

const VERDICTS: TourVerdict[] = ['correct', 'problems', 'inconclusive'];

/**
 * A tour as a whole, above its pictures (the owner, 2026-09-29): where it
 * stands and why, the comment on the tour itself, and the verdict that
 * closes it. The review page of the first artifacts had the comment; nothing
 * had the verdict, so a tour was never done, only each of its pictures.
 *
 * A tour looked at but not marked picture by picture is closed with one
 * button, "reviewed", beside the three verdicts that also say how it went.
 * A tour the platform calls superseded or old is reviewed anyway with one
 * button; one that needs no review is set aside with one sentence saying
 * why, and comes back with another button.
 */
export function TourPanel({
  tour,
  onTour,
}: {
  tour: InspectionTour;
  onTour: (body: InspectionTourRequest) => void;
}) {
  const t = ca.inspectionReviewPage.tour;
  const canReview = useCanReview();
  const [comment, setComment] = useState(tour.comment ?? '');
  const [note, setNote] = useState(tour.note ?? '');
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState('');

  // What the platform answers replaces the drafts only when it changed there.
  useEffect(() => setComment(tour.comment ?? ''), [tour.comment]);
  useEffect(() => setNote(tour.note ?? ''), [tour.note]);

  const why = tourReason(tour);
  const chip = tour.tray === 'done' ? 'ok' : tour.tray === 'review' ? 'warn' : '';
  const canAskFor = tour.tray === 'skip' && (tour.reason === 'superseded' || tour.reason === 'old');
  // Reviewed with nothing more said: a tour of eighty pictures is looked at long
  // before the eightieth is marked (the owner, 2026-09-30).
  const reviewed = tour.verdict === 'reviewed';

  return (
    <section className={`card tour-panel tray-${tour.tray}`} aria-label={t.title}>
      <div className="row between">
        <h2>{t.title}</h2>
        <span className={`chip ${chip}`}>{t.states[tour.state] ?? tour.state}</span>
      </div>
      {tour.platformVersion && <p className="muted small">{t.version(tour.platformVersion)}</p>}
      {why && <p className="tour-reason">{why}</p>}
      {canReview && (canAskFor || tour.dismissed) && (
        <p className="row actions">
          <button type="button" onClick={() => onTour({ reopened: true })}>
            {tour.dismissed ? t.undoDismiss : t.reviewAnyway}
          </button>
        </p>
      )}
      {canReview && (tour.tray === 'review' || reviewed) && (
        <div className="tour-mark-reviewed">
          <p className="row actions">
            <button
              type="button"
              className={reviewed ? 'on' : 'primary'}
              aria-pressed={reviewed}
              onClick={() => onTour({ verdict: reviewed ? '' : 'reviewed', note })}
            >
              <i
                className={`bi ${reviewed ? 'bi-check2-circle' : 'bi-check2'}`}
                aria-hidden="true"
              />{' '}
              {reviewed ? t.markedReviewed : t.markReviewed}
            </button>
          </p>
          <p className="muted small">{t.markReviewedHint}</p>
        </div>
      )}

      <label className="tour-comment">
        {t.comment}
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onBlur={() => {
            if (comment !== (tour.comment ?? '')) onTour({ comment });
          }}
          readOnly={!canReview}
          placeholder={canReview ? t.commentPlaceholder : ''}
          maxLength={4000}
          rows={2}
        />
      </label>

      <fieldset className="tour-verdict">
        <legend>{t.verdict}</legend>
        <div className="row">
          {VERDICTS.map((v) => (
            <button
              key={v}
              type="button"
              className={`chip ${tour.verdict === v ? 'on' : ''}`}
              aria-pressed={tour.verdict === v}
              disabled={!canReview}
              onClick={() => onTour({ verdict: tour.verdict === v ? '' : v, note })}
            >
              {t.verdicts[v]}
            </button>
          ))}
        </div>
        <label>
          <span className="vh">{t.verdictNote}</span>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => {
              if (note !== (tour.note ?? '')) onTour({ note });
            }}
            readOnly={!canReview}
            placeholder={canReview ? t.verdictNote : ''}
            maxLength={2000}
          />
        </label>
        <p className="muted small">
          {tour.verdict && tour.decidedBy
            ? t.decided(tour.decidedBy)
            : canReview
              ? t.verdictHint
              : ''}
        </p>
      </fieldset>

      {canReview && !tour.dismissed && tour.tray !== 'done' && (
        <div className="tour-dismiss">
          {dismissing ? (
            <form
              className="row actions"
              onSubmit={(e) => {
                e.preventDefault();
                if (!reason.trim()) return;
                onTour({ dismissed: true, reason: reason.trim() });
                setDismissing(false);
                setReason('');
              }}
            >
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                aria-label={t.dismissReason}
                placeholder={t.dismissReason}
                maxLength={512}
                required
              />
              <button type="submit" disabled={!reason.trim()}>
                {t.dismissConfirm}
              </button>
              <button type="button" className="link" onClick={() => setDismissing(false)}>
                {ca.cancel}
              </button>
            </form>
          ) : (
            <button type="button" className="link small" onClick={() => setDismissing(true)}>
              {t.dismiss}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
